import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScanLine, FlaskConical, Pill, ArrowRight, Upload } from "lucide-react";

export default function Prescription() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!preview) { toast.error("Upload a prescription image"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/prescriptions/analyze", { image_base64: preview });
      setResult(data.parsed);
      toast.success("Prescription analyzed by AI");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">AI Prescription Reader</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Scan your prescription</h1>
        <p className="text-slate-600 text-sm mt-1">Our AI extracts medicines and tests, then routes them to pharmacy or lab.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-teal-500 transition-colors" data-testid="rx-dropzone">
            <input data-testid="rx-file-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
            {preview ? (
              <img src={preview} alt="Prescription" className="max-h-72 mx-auto rounded-lg" />
            ) : (
              <div>
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-3" />
                <div className="font-semibold">Upload prescription image</div>
                <div className="text-xs text-slate-500 mt-1">PNG, JPG up to 10MB</div>
              </div>
            )}
          </label>
          <div className="mt-4 flex justify-end">
            <Button data-testid="analyze-btn" disabled={!preview || loading} onClick={analyze} className="bg-teal-600 text-white">
              <ScanLine className="w-4 h-4 mr-2" />
              {loading ? "Analyzing…" : "Analyze with AI"}
            </Button>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="font-display text-xl font-bold mb-4">AI extracted results</div>
          {!result && <div className="text-sm text-slate-500">Upload and analyze to see results.</div>}
          {result && (
            <div className="space-y-5">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 mb-2">
                  <Pill className="w-3 h-3" /> Medicines
                </div>
                {result.medicines?.length ? result.medicines.map((m, i) => (
                  <div key={i} data-testid={`rx-med-${i}`} className="p-3 rounded-lg border border-slate-200 mb-2">
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-xs text-slate-600">
                      {[m.dosage, m.frequency, m.duration].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                )) : <div className="text-sm text-slate-500">No medicines detected.</div>}
                {!!result.medicines?.length && (
                  <Button data-testid="order-medicines" size="sm" variant="outline" onClick={() => nav("/pharmacy")}>
                    Order medicines <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 mb-2">
                  <FlaskConical className="w-3 h-3" /> Tests
                </div>
                {result.tests?.length ? result.tests.map((t, i) => (
                  <div key={i} data-testid={`rx-test-${i}`} className="p-3 rounded-lg border border-slate-200 mb-2">
                    <div className="font-semibold">{t.name}</div>
                    {t.instructions && <div className="text-xs text-slate-600">{t.instructions}</div>}
                  </div>
                )) : <div className="text-sm text-slate-500">No tests detected.</div>}
                {!!result.tests?.length && (
                  <Button data-testid="book-tests" size="sm" variant="outline" onClick={() => nav("/lab-tests")}>
                    Book tests <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>

              {result.notes && (
                <div>
                  <Badge variant="outline">Notes</Badge>
                  <p className="text-sm text-slate-700 mt-1">{result.notes}</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
